import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    return {
      isConnected: false,
      notifications: [],
      subscribeToEvent: () => {},
      clearNotification: () => {},
      clearAllNotifications: () => {},
      markAsRead: () => {},
      unreadCount: 0
    };
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Fetch stored notifications from database on mount
  const fetchStoredNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  const connect = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;

    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/${token}`);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'notification') {
            setNotifications(prev => {
              const exists = prev.some(n => n.id === data.data.id);
              if (exists) return prev;
              return [data.data, ...prev];
            });
            
            toast(data.data.title, {
              description: data.data.message,
              action: data.data.link ? {
                label: 'View',
                onClick: () => window.location.href = data.data.link
              } : undefined
            });
          } else if (data.type === 'announcement') {
            toast.info('New Announcement', {
              description: data.data.title
            });
          } else if (data.type === 'payment_update') {
            toast.success('Payment Update', {
              description: data.data.message
            });
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        if (event.code !== 1000 && token) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  }, [token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const subscribeToEvent = useCallback((eventId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        event_id: eventId
      }));
    }
  }, []);

  const clearNotification = useCallback(async (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (token) {
      try {
        await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* silent */ }
    }
  }, [token]);

  const clearAllNotifications = useCallback(async () => {
    setNotifications([]);
    if (token) {
      try {
        await axios.put(`${API_URL}/api/notifications/read-all`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* silent */ }
    }
  }, [token]);

  const markAsRead = useCallback(async (notificationId) => {
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, is_read: true } : n
    ));
    if (token) {
      try {
        await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* silent */ }
    }
  }, [token]);

  // Connect WebSocket and fetch stored notifications
  useEffect(() => {
    if (token && user) {
      fetchStoredNotifications();
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [token, user, connect, disconnect, fetchStoredNotifications]);

  const value = {
    isConnected,
    notifications,
    subscribeToEvent,
    clearNotification,
    clearAllNotifications,
    markAsRead,
    unreadCount: notifications.filter(n => !n.is_read).length
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
